// components/ui/search/list-search.js
import React, { useCallback, useRef, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import SearchMdIcon from 'src/icons/untitled-ui/free/search-md';
import { Stack, Box, Typography, OutlinedInput, InputAdornment } from '@mui/material';
import { IconButton, SvgIcon, Menu, MenuItem } from '@mui/material';
import FilterListIcon from 'src/icons/untitled-ui/free/filter-lines';
import ClearIcon from 'src/icons/untitled-ui/free/x-clear';
import DynamicSearchDialog from './list-search-dialog';

const labels = {
  pre: { short: 'Prefix', full: 'Search (ends with value)' },
  both: { short: 'Both', full: 'Search (contains value)' },
  sub: { short: 'Suffix', full: 'Search (starts with value)' },
  val: { short: 'Value', full: 'Search (value or number)' },
  str: { short: 'String', full: 'Search (split to array by comma)' },
  arr: { short: 'Array', full: 'Search by [value1][value2]' },
  date: { short: 'Date', full: 'Search by date' }
};

const ListSearch = ({
  onFiltersChange,
  filtersQuery,
  placeholder = 'Search',
  filterLabels = labels,
  disable = false,
  triggerSearch,
  onHistory,
}) => {
  const queryRef = useRef(null);
  const formRef = useRef(null);
  const resetRef = useRef();

  const [filters, setFilters] = useState({});
  const [inputs, setInputs] = useState({});
  const [selectedFilter, setSelectedFilter] = useState(`${placeholder}`);
  const [flag, setFlag] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [open, setOpen] = useState(false);
  const FILTER_CACHE_KEY = 'listSearch.lastFilter';

  const handleFiltersUpdate = useCallback(() => {
    onFiltersChange?.(filters);
  }, [filters, onFiltersChange]);

  useEffect(() => {
    handleFiltersUpdate();
  }, [filters, handleFiltersUpdate]);

  const handleSubmitted = useCallback(
    (event, updatedInputs = inputs) => {
      if (event) event.preventDefault();
      const processedQuery = preprocessQuery(updatedInputs);
      setFilters((prevFilters) => ({
        ...prevFilters,
        query: processedQuery,
      }));
    },
    [inputs]
  );

  const handleQueryChange = useCallback(
    (event) => {
      const { value } = event.target;
      setInputs((prevInputs) => {
        const updatedInputs = { ...prevInputs, [selectedFilter]: value };
        setFlag(Object.values(updatedInputs).some((val) => val?.trim?.() !== ''));
        return updatedInputs;
      });
    },
    [selectedFilter]
  );

  const handleClearField = useCallback((event) => {
    event.preventDefault(); // Prevent default behavior of the button
    // Reset the form fields
    if (formRef.current) {
      formRef.current.reset();
      //formRef.current.requestSubmit();
    }
    // Reset the dialog preference
    if (resetRef.current) {
      resetRef.current(['date']);
    }

    setInputs((prevInputs) => {
      const updatedInputs = { ...prevInputs };
      // Skip clearing keys that exist in triggerSearch
      if (!triggerSearch || !triggerSearch.hasOwnProperty(selectedFilter)) {
        delete updatedInputs[selectedFilter]; // Remove only the current field's data if not in triggerSearch
      }
      
      // Update the flag: Check if any input value is non-empty
      setFlag(Object.values(updatedInputs).some((val) => val?.trim?.() !== ''));

      handleSubmitted(event, updatedInputs);
      return updatedInputs;
    });
  }, [selectedFilter]);

  const handleSelectFilter = (filter) => {
    if (filter === 'date') {
      setOpen(true);
    } 
    setSelectedFilter(filter);
    localStorage.setItem(FILTER_CACHE_KEY, filter); // Save to cache
    setAnchorEl(null);
  };

  const handleDialogClose = () => {
    setOpen(false);
  };

  useEffect(() => {
    if (filtersQuery && Object.keys(filtersQuery).length > 0) {
      setInputs(filtersQuery);
      setFlag(Object.values(filtersQuery).some((val) => val?.trim?.() !== ''));
    }
  }, [filtersQuery]);

  useEffect(() => {
    if (filtersQuery && Object.keys(filtersQuery).length > 0) {
      onHistory?.(filtersQuery);
    } else {
      localStorage.removeItem(FILTER_CACHE_KEY); // Clear cache
    }
  }, []);

  useEffect(() => {
    if (triggerSearch && Object.keys(triggerSearch).length > 0) {
      setInputs((prevInputs) => {
        const updatedInputs = { ...prevInputs, ...triggerSearch };
        // You can trigger the handleSubmitted here with the updated inputs
        handleSubmitted({ preventDefault: () => {} }, updatedInputs); // Pass updatedInputs directly
        return updatedInputs; // Return the updated state
      });
    }
  }, [triggerSearch]);

  useEffect(() => {
    const cached = localStorage.getItem(FILTER_CACHE_KEY);
    if (cached && filterLabels[cached]) {
      setSelectedFilter(cached);
    } else {
      setSelectedFilter(placeholder); // fallback
    }
  }, []);
  
  return (
    <Stack
      alignItems="center"
      direction="row"
      flexWrap="wrap"
      spacing={1}
      sx={{ p: 1, ...(disable && { pointerEvents: 'none', opacity: 0.5 }) }}
    >
      <Box
        component="form"
        onSubmit={handleSubmitted}
        sx={{ flexGrow: 1 }}
        ref={formRef}
      >
        <OutlinedInput
          value={inputs[selectedFilter] || ''}
          onChange={handleQueryChange}
          fullWidth
          inputProps={{ ref: queryRef }}
          placeholder={filterLabels[selectedFilter]?.full || placeholder}
          startAdornment={
            <InputAdornment position="start">
              {(inputs[selectedFilter] || '').trim() ? (
                <SvgIcon 
                  onClick={handleClearField} 
                  sx={{
                    cursor: 'pointer',
                    '&:hover': { color: 'primary.main' },
                    color: 'primary.main',
                  }}>
                  <ClearIcon />
                </SvgIcon>
              ) : (
                <SvgIcon>
                  <SearchMdIcon />
                </SvgIcon>
              )}
            </InputAdornment>
          }
          endAdornment={
            <InputAdornment position="end">
              {selectedFilter && (
                <Typography variant="caption" sx={{ mr: 1 }}>
                  {filterLabels[selectedFilter]?.short || selectedFilter}
                </Typography>
              )}
              <IconButton onClick={(e) => setAnchorEl(e.currentTarget)}>
                <FilterListIcon 
                  sx={{
                    cursor: 'pointer',
                    '&:hover': { color: 'primary.main' },
                    color: 'text.primary',
                  }} />
              </IconButton>
              <IconButton
                onClick={handleSubmitted}
                sx={{
                  color: Object.values(inputs).some((val) => val?.trim?.() !== '')
                    ? 'primary.main' // Highlight color when inputs have data
                    : 'inherit', // Default color
                }}
              >
                <SearchMdIcon />
              </IconButton>
              <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={() => setAnchorEl(null)}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'right'
                }}
              >
                {Object.keys(filterLabels).map((key) => (
                  <MenuItem key={key} onClick={() => handleSelectFilter(key)}>
                    {filterLabels[key]?.full || key}
                  </MenuItem>
                ))}
              </Menu>
            </InputAdornment>
          }
        />
      </Box>

      <DynamicSearchDialog
        open={open}
        handleClose={handleDialogClose}
        selectedFilter={selectedFilter}
        updateInput={(data) => setInputs((prev) => ({ ...prev, ...data }))}
        triggerReset={(callback) => {
          resetRef.current = callback; // Store callback reference
        }}
      />
    </Stack>
  );
};

ListSearch.propTypes = {
  onFiltersChange: PropTypes.func,
};

const preprocessQuery = (obj) => {
  return Object.keys(obj).length ? obj : null;
};

export default ListSearch;
